import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SliderThumbDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getElevationLayers
} from "../../../../helpers/theme/elevation";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

const stylesheet = StyleSheet.create({
    disabled: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    verticalTip: {
        justifyContent: "center",
        alignItems: "flex-start"
    },
    pressed: {
        ...webStyle({
            cursor: "grabbing"
        })
    },
    tip: {
        pointerEvents: "none",
        position: "absolute"
    },
    container: {
        position: "absolute",
        ...webStyle({
            cursor: "grab"
        })
    },
    horizontalTip: {
        alignItems: "center"
    },
    bubbleText: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    bubble: {
        flexShrink: 0
    }
});

export const useStyles = ({
    currentMeasures,
    isFocusVisible,
    currentColors,
    shapeConfigs,
    touchTargets,
    orientation,
    pointerType,
    shadowAlpha,
    tipDistance,
    elevations,
    isDisabled,
    isHovered,
    isPressed,
    radiuses,
    borders,
    colors,
    spaces,
    offset
}: SliderThumbDynamicStyleType) => {
    const centerOffset = (currentMeasures.row - currentMeasures.thumbSize) / 2;

    const tipExtra = pointerType === "coarse" ? (touchTargets.coarse - currentMeasures.thumbSize) / 2 : 0;

    const styles = {
        container: {
            zIndex: isPressed ? shapeConfigs.layers.raised + 1 : shapeConfigs.layers.raised,
            borderColor: currentColors.thumbBorderColor,
            backgroundColor: currentColors.thumbColor,
            height: currentMeasures.thumbSize,
            width: currentMeasures.thumbSize,
            borderWidth: borders.selection,
            borderRadius: radiuses.full
        } as Mutable<ViewStyle>,
        bubble: {
            backgroundColor: colors.content.container.inverse,
            paddingHorizontal: spaces.spacingSm,
            paddingVertical: spaces.spacingXs,
            ...getCornerStyle({
                radius: radiuses.form,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        tip: {
            width: shapeConfigs.tooltipMaxWidth
        } as Mutable<ViewStyle>
    };

    if(orientation === "vertical") {
        Object.assign(styles.container, {
            start: centerOffset,
            bottom: offset
        });

        Object.assign(styles.tip, {
            start: currentMeasures.thumbSize + tipExtra + spaces.spacingSm,
            height: currentMeasures.thumbSize,
            top: tipDistance
        });
    }

    if(orientation === "horizontal") {
        Object.assign(styles.container, {
            top: centerOffset,
            start: offset
        });

        Object.assign(styles.tip, {
            start: (currentMeasures.thumbSize - shapeConfigs.tooltipMaxWidth) / 2 - tipDistance,
            bottom: currentMeasures.thumbSize + tipExtra + spaces.spacingSm
        });
    }

    const shadows = isDisabled ? [] : getElevationLayers({
        shadowColor: colors.system.shadow,
        level: "level1",
        shadowAlpha,
        elevations
    });

    const ringColor = isPressed ? currentColors.pressedRingColor : currentColors.hoverRingColor;

    const isRing = !isDisabled && (isPressed || isHovered && pointerType === "fine");

    if(isRing) {
        shadows.push({
            spreadDistance: borders.indicator,
            color: ringColor,
            offsetX: 0,
            offsetY: 0
        });
    }

    if(shadows.length) {
        Object.assign(styles.container, {
            boxShadow: shadows
        });
    }

    Object.assign(styles.container, getFocusRingStyle({
        colorKey: currentColors.focusKey,
        isFocusVisible,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
